import { Box, Flex, Spinner, Text, useDisclosure } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  AuctionProposalSortDirection,
  CreditAuctionProposalFilters,
  CreditAuctionProposalListResponse,
  ResponseAuctionProposalDto,
  VISIBLE_AUCTION_PROPOSAL_STATUSES,
} from "@/models/credit-auction-proposal.model";
import CreditAuctionProposalsPagination from "./credit-auction-proposals-pagination";
import CreditAuctionProposalsTable from "./credit-auction-proposals-table";
import CreditAuctionProposalDetailModal from "./credit-auction-proposal-detail-modal";
import { getCreditAuctionProposals } from "@/services/creditAuctionProposal.service";

const parseOptionalNumber = (value: string): number | undefined => {
  if (!value.trim()) {
    return undefined;
  }
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const parseMarginToApi = (
  value: string,
  extremeValue: number,
): number | undefined => {
  const parsedValue = parseOptionalNumber(value);
  if (parsedValue === undefined || parsedValue === extremeValue) {
    return undefined;
  }
  return parsedValue;
};

interface CreditAuctionProposalsContentProps {
  appliedSearchTerm: string;
  appliedFilters: CreditAuctionProposalFilters;
  /** Incrementa quando o usuário clica em "Filtrar", forçando refetch. */
  applyCounter: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Limites usados no parser de margem (mesmo do header). */
  minMarginRange: number;
  maxMarginRange: number;
  /** Repassa pro header o total retornado pela API, pra renderizar o "Total de X propostas". */
  onTotalItemsChange?: (total: number) => void;
}

/**
 * Body da aba "Leilão interno": só tabela + paginação + modal de detalhe.
 * Os filtros e o título ficam no orquestrador (CreditAuctionProposalsPage)
 * — segue o padrão do CorbansTable.
 */
export default function CreditAuctionProposalsContent({
  appliedSearchTerm,
  appliedFilters,
  applyCounter,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  minMarginRange,
  maxMarginRange,
  onTotalItemsChange,
}: CreditAuctionProposalsContentProps) {
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(
    null,
  );
  const [validadeSortDirection, setValidadeSortDirection] =
    useState<AuctionProposalSortDirection | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data, isLoading } = useQuery<CreditAuctionProposalListResponse>({
    queryKey: [
      "credit-auction-proposals",
      page,
      pageSize,
      appliedSearchTerm,
      appliedFilters,
      applyCounter,
      validadeSortDirection,
    ],
    queryFn: () =>
      getCreditAuctionProposals({
        page,
        pageSize,
        searchTerm: appliedSearchTerm,
        statuses: appliedFilters.statuses.length
          ? appliedFilters.statuses
          : VISIBLE_AUCTION_PROPOSAL_STATUSES,
        wonAuction: appliedFilters.wonAuction ?? undefined,
        startDate: appliedFilters.startDate || undefined,
        endDate: appliedFilters.endDate || undefined,
        validadeStartDate: appliedFilters.validadeStartDate || undefined,
        validadeEndDate: appliedFilters.validadeEndDate || undefined,
        minReleasedValue: parseMarginToApi(
          appliedFilters.minReleasedValue,
          minMarginRange,
        ),
        maxReleasedValue: parseMarginToApi(
          appliedFilters.maxReleasedValue,
          maxMarginRange,
        ),
        negotiationSubStep: appliedFilters.negotiationSubStep ?? undefined,
        sortBy: validadeSortDirection ? "data_validade" : undefined,
        sortDirection: validadeSortDirection ?? undefined,
      }),
  });

  const proposals = data?.data ?? [];
  const totalPages = data?.total_paginas ?? 1;
  const currentPage = data?.pagina_atual ?? 1;
  const totalItems = data?.total ?? 0;

  useEffect(() => {
    onTotalItemsChange?.(totalItems);
  }, [totalItems, onTotalItemsChange]);

  const handleToggleValidadeSort = (): void => {
    setValidadeSortDirection((previous) =>
      previous === null ? "asc" : previous === "asc" ? "desc" : null,
    );
    onPageChange(1);
  };

  const handleOpenDetails = (proposal: ResponseAuctionProposalDto): void => {
    setSelectedProposalId(proposal.id);
    onOpen();
  };

  const handleCloseDetails = (): void => {
    onClose();
    setSelectedProposalId(null);
  };

  return (
    <Box w="100%">
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        px={0}
        pt={{ base: 1, md: 1 }}
        pb={{ base: 2, md: 4 }}
      >
        <CreditAuctionProposalsPagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          mt={2}
          mb={2}
        />

        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" minH="260px">
            <Spinner size="xl" color="gray.500" />
          </Flex>
        ) : proposals.length ? (
          <CreditAuctionProposalsTable
            proposals={proposals}
            onOpenDetails={handleOpenDetails}
            validadeSort={validadeSortDirection}
            onToggleValidadeSort={handleToggleValidadeSort}
          />
        ) : (
          <Flex justifyContent="center" alignItems="center" minH="260px">
            <Text color="gray.500" fontFamily="Lexend Deca">
              Nenhuma proposta encontrada para os filtros informados.
            </Text>
          </Flex>
        )}

        <CreditAuctionProposalsPagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          mt={5}
          mb={1}
        />
      </Box>

      <CreditAuctionProposalDetailModal
        isOpen={isOpen}
        onClose={handleCloseDetails}
        proposalId={selectedProposalId}
        proposal={null}
      />
    </Box>
  );
}
